// Utility functions for transforming chart data

/**
 * Utility function to transform API data into pie chart format
 * @param {Array} chartsData - API response containing charts data
 * @returns {Object|null} - Formatted data for the pie chart or null if no data found
 */
export const transformPieChartData = (chartsData: any, primaryColor: string) => {
  // Find the first chart with type "PIE_CHART"
  const pieChart = chartsData.find(
    (chart: { type: string }) => chart.type === "PIE_CHART"
  );

  if (!pieChart) {
    console.warn("No pie chart data found in the API response");
    return null;
  }

  // Extract relevant information
  const { title, tooltip, components } = pieChart;

  // Usually in a pie chart, we have labels on X axis and values on Y axis
  const xAxisComponent = components.find(
    (comp: { axis: string }) => comp.axis === "X"
  );
  const yAxisComponent = components.find(
    (comp: { axis: string }) => comp.axis === "Y"
  );

  if (!xAxisComponent || !yAxisComponent) {
    console.warn("Missing X or Y axis components in pie chart data");
    return null;
  }

  // Create a map to match labels with values using IDs
  const labels: string[] = [];
  const values: number[] = [];
  const xValues = xAxisComponent.values;

  // For each X value (label), find the corresponding Y value
  xValues.forEach((xItem: { value: string; id: any }) => {
    // Add the label
    labels.push(xItem.value);

    // Find the corresponding value in Y axis data
    const matchingYValue = yAxisComponent.values.find(
      (yItem: { id: any }) => yItem.id === xItem.id
    );

    if (matchingYValue) {
      // Convert to number and add to values array
      values.push(parseFloat(matchingYValue.value));
    } else {
      console.warn(
        `No matching value found for label ${xItem.value} with ID ${xItem.id}`
      );
      values.push(0); // Default to 0 if no matching value
    }
  });

  // Generate a color palette
  const colors = generateColorPalette(labels.length, primaryColor);

  return {
    title,
    tooltip,
    chartData: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderWidth: 1,
        },
      ],
    },
  };
};

/**
 * Utility function to transform API data into bar chart format with dual y-axes
 * @param {Array} chartsData - API response containing charts data
 * @returns {Object|null} - Formatted data for the bar chart or null if no data found
 */
export const transformBarChartData = (chartsData: any, primaryColor: string) => {
    const barChart = chartsData.find((chart: any) => chart.type === "MULTI_AXIS_BAR_CHART");

    if (!barChart) {
        console.warn("No bar chart data found in the API response");
        return null;
    }

    const { title, tooltip, components } = barChart;

    const xAxisComponent = components.find((comp: any) => comp.axis === "X");
    const yAxisComponent = components.find((comp: any) => comp.axis === "Y");
    const y1AxisComponent = components.find((comp: any) => comp.axis === "Y1");

    if (!xAxisComponent || !yAxisComponent) {
        console.warn("Missing X or Y axis components in bar chart data");
        return null;
    }

    const labels = xAxisComponent.values.map((item: { value: any }) => item.value);
const colors = generateColorPalette(2, primaryColor);
    const datasets: {
        label: string;
        data: number[];
        backgroundColor: string;
        borderColor: string;
        borderWidth: number;
        yAxisID: string;
    }[] = [];

    if (yAxisComponent) {
        const yData: number[] = [];
        xAxisComponent.values.forEach((xItem: { id: any }) => {
            const matchingYValue = yAxisComponent.values.find((yItem: { id: any }) => yItem.id === xItem.id);
            yData.push(matchingYValue ? parseFloat(matchingYValue.value) : 0);
        });

        datasets.push({
            label: yAxisComponent.label,
            data: yData,
            backgroundColor: colors[0],
            borderColor: colors[0],
            borderWidth: 1,
            yAxisID: "y",
        });
    }

    if (y1AxisComponent) {
        const y1Data: number[] = [];
        xAxisComponent.values.forEach((xItem: { id: any }) => {
            const matchingY1Value = y1AxisComponent.values.find((y1Item: { id: any }) => y1Item.id === xItem.id);
            y1Data.push(matchingY1Value ? parseFloat(matchingY1Value.value) : 0);
        });

        datasets.push({
            label: y1AxisComponent.label,
            data: y1Data,
            backgroundColor: colors[1],
            borderColor: colors[1],
            borderWidth: 1,
            yAxisID: "y1",
        });
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 5,
                bottom: 5,
                left: 5,
                right: 5,
            },
        },
        scales: {
            y: {
                grid: {
                    display: false,
                },
                type: "linear" as const,
                position: "left" as const,
                beginAtZero: true,
                title: {
                    display: true,
                    text: yAxisComponent?.label,
                },
                ticks: {
                    maxTicksLimit: 5,
                    padding: 5
                }
            },
            y1: {
                type: "linear" as const,
                position: "right" as const,
                beginAtZero: true,
                title: {
                    display: true,
                    text: y1AxisComponent?.label,
                },
                grid: {
                    drawOnChartArea: false,
                    display: false,
                },
                ticks: {
                    maxTicksLimit: 5,
                    padding: 5
                }
            },
            x: {
                grid: {
                    display: false,
                },
                title: {
                    display: false,
                    text: xAxisComponent.label,
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    padding: 5,
                    callback: function(value, index) {
                      // For Chart.js 2, we work with the labels array directly
                      const labels = (this as any).chart.data.labels;
                      const label = labels[index];
                      const maxLength = 12; // Adjust this value based on your needs
                      
                      if (label && label.length > maxLength) {
                        return label.substring(0, maxLength) + '...';
                      }
                      return label;
                    }
                }
            },
        },
        plugins: {
            legend: {
                display: false,
                position: 'top' as const,
                align: 'end' as const,
                labels: {
                    boxWidth: 10,
                    padding: 6
                }
            },
            title: {
                display: false,
                text: title,
            },
            tooltip: {
              intersect: false
            }
        },
    };

    return {
        title,
        tooltip,
        chartData: {
            labels,
            datasets,
        },
        options,
    };
};

/**
 * Utility function to transform API data into simple bar chart format
 * @param {Array} chartsData - API response containing charts data
 * @returns {Object|null} - Formatted data for the bar chart or null if no data found
 */
export const transformSimpleBarChartData = (chartsData: any, primaryColor: string) => {
    const barChart = chartsData.find((chart: any) => chart.type === "BAR_CHART");

    if (!barChart) {
        console.warn("No bar chart data found in the API response");
        return null;
    }

    const { title, tooltip, components } = barChart;

    const xAxisComponent = components.find((comp: any) => comp.axis === "X");
    const yAxisComponent = components.find((comp: any) => comp.axis === "Y");

    if (!xAxisComponent || !yAxisComponent) {
        console.warn("Missing X or Y axis components in bar chart data");
        return null;
    }

    const labels = xAxisComponent.values.map((item: { value: any }) => item.value);
    const colors = generateColorPalette(labels.length, primaryColor);
    const datasets = [{
        label: yAxisComponent.label,
        data: yAxisComponent.values.map((item: { value: any }) => parseFloat(item.value)),
        backgroundColor: colors[0],
        borderColor: colors[0],
        borderWidth: 1,
    }];

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 5,
                bottom: 5,
                left: 5,
                right: 5,
            },
        },
        scales: {
            y: {
                grid: {
                    display: false,
                },
                type: "linear" as const,
                position: "left" as const,
                beginAtZero: true,
                title: {
                    display: true,
                    text: yAxisComponent?.label,
                },
                ticks: {
                    maxTicksLimit: 5,
                    padding: 5
                }
            },
            x: {
                grid: {
                    display: false,
                },
                title: {
                    display: false,
                    text: xAxisComponent.label,
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    padding: 5,
                    callback: function(value, index) {
                      const labels = (this as any).chart.data.labels;
                      const label = labels[index];
                      const maxLength = 8;
                      
                      if (label && label.length > maxLength) {
                        return label.substring(0, maxLength) + '...';
                      }
                      return label;
                    }
                }
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
                text: title,
            },
            tooltip: {
              intersect: false
            }
        }
    };

    return {
        title,
        tooltip,
        chartData: {
            labels,
            datasets,
        },
        options,
    };
};

/**
 * Generate a color palette with different shades
 * @param {number} count - Number of colors needed
 * @returns {Array} - Array of color strings
 */
export const generateColorPalette = (count: any, primaryColor: string) => {
  const baseColors = generateColorFamily(primaryColor, count);

  // If we need more colors than we have in our base set, we'll loop through them
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(baseColors[i % baseColors.length]);
  }

  return colors;
};

/**
 * Generates an array of colors based on a primary color for use in Chart.js
 * @param {string} primaryColor - The primary color in hex format (e.g., "#3366CC")
 * @param {number} count - Number of colors needed
 * @param {Object} options - Additional options for color generation
 * @param {number} options.saturationRange - Range of saturation variation [0-1] (default: 0.3)
 * @param {number} options.lightnessRange - Range of lightness variation [0-1] (default: 0.4)
 * @returns {Array} Array of hex color codes
 */
function generateColorFamily(
  primaryColor = "#273896",
  count,
  options = { saturationRange: 0.1, lightnessRange: 0.6 } // Further adjusted ranges for even lighter colors
) {
  // Convert hex to HSL
  const hexToHSL = (hex) => {
    // Remove the # if present
    hex = hex.replace(/^#/, "");

    // Parse the hex values
    let r = parseInt(hex.slice(0, 2), 16) / 255;
    let g = parseInt(hex.slice(2, 4), 16) / 255;
    let b = parseInt(hex.slice(4, 6), 16) / 255;

    // Find max and min RGB values
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      // Achromatic
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }

      h /= 6;
    }

    return { h, s, l };
  };

  // Convert HSL to hex
  const hslToHex = (h, s, l) => {
    // Keep h, s, l within valid ranges
    h = Math.max(0, Math.min(1, h));
    s = Math.max(0, Math.min(1, s));
    l = Math.max(0, Math.min(1, l));

    let r, g, b;

    if (s === 0) {
      // Achromatic
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    // Convert to hex
    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // Get HSL values of primary color
  const { h, s, l } = hexToHSL(primaryColor);

  // Generate color array
  const colors: string[] = [];

  // Distribution strategy for better visual appeal
  const generateStrategicColors = () => {
    // For small counts, we want more differentiation
    if (count <= 3) {
      // For few colors, vary lightness more
      for (let i = 0; i < count; i++) {
        const newL = Math.max(
          0.5, // Further adjusted minimum lightness for even lighter colors
          Math.min(
            0.95, // Further adjusted maximum lightness for even lighter colors
            l -
              options.lightnessRange / 2 +
              (i * options.lightnessRange) / (count - 1 || 1)
          )
        );
        colors.push(hslToHex(h, s, newL));
      }
    } else {
      // For larger sets, use a more complex distribution
      // First color is the primary
      colors.push(primaryColor);

      // Then alternate between lighter and darker
      const step = Math.min(options.lightnessRange / Math.ceil(count / 2), 0.3); // Further adjusted step for even lighter colors
      const satStep = Math.min(
        options.saturationRange / Math.ceil(count / 2),
        0.05 // Further adjusted step for even less saturation variation
      );

      for (let i = 1; i < count; i++) {
        let newL, newS;

        if (i % 2 === 1) {
          // Lighter variants
          newL = Math.min(0.95, l + step * Math.ceil(i / 2));
          newS = Math.max(0.1, s - satStep * Math.ceil(i / 2)); // Further adjusted minimum saturation for even less contrast
        } else {
          // Darker variants
          newL = Math.max(0.5, l - step * (i / 2)); // Further adjusted minimum lightness for even lighter colors
          newS = Math.min(0.7, s + satStep * (i / 2)); // Further adjusted maximum saturation for even less contrast
        }

        colors.push(hslToHex(h, newS, newL));
      }
    }
  };

  generateStrategicColors();

  return colors;
}
